import { LOCAL_STORAGE_KEYS } from 'src/tpbp-core/models/common.const'
import { logger } from 'src/utils/logger.utils'
import { MESSAGE_TYPE } from 'src/utils/sendMessage.const'
import { sendPostMessage } from 'src/utils/sendMessage.utils'

import {
  LCL_BRIDGE_COMMAND,
  LCL_BRIDGE_PROTOCOL,
  LCL_INIT_CHANNEL_MESSAGE,
  LCL_REFRESH_CHANNEL_MESSAGE,
  LCL_BROADCAST_CHANNEL_NAME,
  type LclBridgeCommand,
  type LclBridgeEvent,
  type LclBridgeProtocol,
} from './LclBridge.const'
import type {
  BridgeCommandMessage,
  BridgeEventMessage,
  CloseContainerData,
} from './LclBridge.types'

export class LclBridgeService {
  private port2: MessagePort | undefined = undefined
  private protocol: LclBridgeProtocol = LCL_BRIDGE_PROTOCOL.IFRAME
  private eventListeners: Map<LclBridgeEvent, Array<(data?: unknown) => void>> =
    new Map()

  /**
   * Initialise l'écouteur global pour intercepter la création du MessageChannel (INIT_CHANNEL)
   */
  public init(): void {
    window.addEventListener('message', this.handleWindowMessage)
  }

  /**
   * Détruit le service, ferme les ports et arrête les intervalles
   */
  public destroy(): void {
    window.removeEventListener('message', this.handleWindowMessage)
    if (this.port2) {
      this.port2.close()
    }
  }

  private handleWindowMessage = (event: MessageEvent): void => {
    if (
      event.data === LCL_INIT_CHANNEL_MESSAGE &&
      event.ports &&
      event.ports.length > 0
    ) {
      this.port2 = event.ports[0]
      this.protocol = LCL_BRIDGE_PROTOCOL.WEBVIEW

      if (this.port2) {
        this.port2.addEventListener('message', this.handlePortMessage)
        this.port2.start()
      }
      logger.log('LclBridgeService: Webview MessageChannel initialized')
    }
  }

  private handlePortMessage = (event: MessageEvent): void => {
    try {
      // Selon l'implémentation native, data peut déjà être un objet ou nécessiter un parse JSON
      const message =
        typeof event.data === 'string'
          ? (JSON.parse(event.data) as BridgeEventMessage)
          : (event.data as BridgeEventMessage)

      if (message && message.type) {
        this.dispatchEvent(message.type, message.data)
      }
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      logger.error('LclBridgeService: Error parsing port message', error)
    }
  }

  /**
   * Mécanisme de secours (BroadcastChannel) pour demander un nouveau port2 à l'application Native
   */
  public requestPort2FromNativeApp(): void {
    if (this.port2) {
      this.port2.close()
      this.port2 = undefined
    }

    if ('BroadcastChannel' in window) {
      const broadcastChannel = new BroadcastChannel(LCL_BROADCAST_CHANNEL_NAME)
      // eslint-disable-next-line unicorn/require-post-message-target-origin
      broadcastChannel.postMessage(LCL_REFRESH_CHANNEL_MESSAGE)
      // On ferme le channel de broadcast juste après avoir envoyé le message
      setTimeout(() => {
        broadcastChannel.close()
      }, 1000)
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      logger.warn(
        'LclBridgeService: BroadcastChannel is not supported on this browser.',
      )
    }
  }

  /**
   * Envoie une commande vers l'application hôte (Native Webview ou Iframe Parent)
   * @param command La commande LclBridgeCommand
   * @param data Le payload optionnel
   */
  public sendCommand<T>(command: LclBridgeCommand, data?: T): void {
    if (this.protocol === LCL_BRIDGE_PROTOCOL.WEBVIEW && this.port2) {
      const payload: BridgeCommandMessage<T> = { type: command }
      if (data !== undefined) {
        payload.data = data
      }
      // eslint-disable-next-line unicorn/require-post-message-target-origin
      this.port2.postMessage(payload)
    } else {
      this.handleIframeFallback(command, data)
    }
  }

  private handleIframeFallback<T>(command: LclBridgeCommand, data?: T): void {
    const targetOrigin =
      localStorage.getItem(LOCAL_STORAGE_KEYS.TARGET_ORIGIN) ?? ''

    if (command === LCL_BRIDGE_COMMAND.HEARTBEAT) {
      sendPostMessage(MESSAGE_TYPE.windowActive, targetOrigin)
    } else if (command === LCL_BRIDGE_COMMAND.CLOSE_CONTAINER_COMMAND) {
      const closeData = data as unknown as CloseContainerData
      const type =
        closeData?.status === 'abort'
          ? MESSAGE_TYPE.endAbort
          : MESSAGE_TYPE.endSuccess
      const payload = closeData?.payload || {}
      sendPostMessage(type, targetOrigin, payload)
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      logger.warn(
        `LclBridgeService: Command ${command} not supported in IFRAME fallback mode.`,
      )
    }
  }

  /**
   * Abonne un callback à un événement venant de la Webview Native
   */
  public addEventListener(
    event: LclBridgeEvent,
    callback: (data?: unknown) => void,
  ): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, [])
    }
    this.eventListeners.get(event)?.push(callback)
  }

  /**
   * Désabonne un callback
   */
  public removeEventListener(
    event: LclBridgeEvent,
    callback: (data?: unknown) => void,
  ): void {
    const callbacks = this.eventListeners.get(event)
    if (callbacks) {
      this.eventListeners.set(
        event,
        callbacks.filter((callbackToRemove) => callbackToRemove !== callback),
      )
    }
  }

  private dispatchEvent(event: LclBridgeEvent, data?: unknown): void {
    const callbacks = this.eventListeners.get(event)
    if (callbacks) {
      for (const callback of callbacks) {
        try {
          callback(data)
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call
          logger.error(
            `LclBridgeService: Error in event listener for ${event}`,
            error,
          )
        }
      }
    }
  }
}

export const lclBridgeService = new LclBridgeService()
